import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { PLANS } from "../constants/plans";
import "../Css/Pricing.css";

const Pricing = () => {
  const { currentUser } = useAuth();
  const { profile } = useSubscription();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState("");

  const currentPlan = profile?.plan || "free";

  const handleSubscribe = async (plan) => {
    if (!currentUser) {
      navigate(`/signup?plan=${plan.id}`);
      return;
    }

    if (currentPlan === plan.id) return;

    setError("");
    setLoadingPlan(plan.id);

    try {
      const response = await fetch("/api/create-subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planId: plan.id,
          razorpayPlanId: plan.razorpayPlanId,
          userId: currentUser.id,
          email: currentUser.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || "Failed to initiate payment");

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        subscription_id: data.subscriptionId,
        name: "Manognaa Solutions",
        description: `${plan.name} Plan — ₹${plan.price.toLocaleString("en-IN")}/month`,
        image: "/logo192.png",
        prefill: {
          email: currentUser.email,
        },
        theme: { color: "#8B008B" },
        handler: function (response) {
          navigate("/checkout/success", {
            state: {
              planName: plan.name,
              planId: plan.id,
              paymentId: response.razorpay_payment_id,
            },
          });
        },
        modal: {
          ondismiss: function () {
            setLoadingPlan(null);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setError(err.message);
      setLoadingPlan(null);
    }
  };

  const getCtaLabel = (plan) => {
    if (!currentUser) return "Get Started";
    if (currentPlan === plan.id) return "Current Plan";
    if (loadingPlan === plan.id) return "Opening checkout...";
    return "Subscribe Now";
  };

  return (
    <div className="pricingPage">
      <div className="pricingHeader">
        <h1>Simple, transparent pricing</h1>
        <p>
          Build and deploy custom AI agents, automate workflows, and transform
          your business — billed monthly in INR, cancel anytime.
        </p>
      </div>

      {error && (
        <div style={{ textAlign: "center", color: "#b91c1c", marginBottom: "1em", fontFamily: "Poppins, sans-serif" }}>
          {error}
        </div>
      )}

      <div className="pricingGrid">
        {PLANS.map((plan) => (
          <div
            key={plan.id}
            className={`pricingCard${plan.highlighted ? " highlighted" : ""}`}
          >
            {plan.badge && <div className="planBadge">{plan.badge}</div>}

            <h3>{plan.name}</h3>
            <p className="planTagline">{plan.tagline}</p>

            <div className="planPrice">
              <span className="currency">₹</span>
              {plan.price.toLocaleString("en-IN")}
              <span className="cycle">/mo</span>
            </div>

            <hr className="planDivider" />

            <ul className="planFeatures">
              {plan.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            {currentPlan === plan.id ? (
              <div className="planCtaCurrent">Current Plan ✓</div>
            ) : (
              <button
                className="planCta"
                onClick={() => handleSubscribe(plan)}
                disabled={loadingPlan !== null && loadingPlan !== plan.id}
              >
                <span>{getCtaLabel(plan)}</span>
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="pricingNote">
        All plans include GST. Need a custom quote?{" "}
        <Link to="/contact">Contact us</Link>.
        {!currentUser && (
          <>
            {" "}Already have an account?{" "}
            <Link to="/login">Log in</Link>
          </>
        )}
      </p>
    </div>
  );
};

export default Pricing;
